/**
 * THE WORDS OF THE SHELL — the top bar, the account menu and the ⌘K dialogs.
 *
 * Same rule as the rest of the frame: English is the origin, pt-BR the
 * translation, and nothing here is a feature's word. What a screen INSIDE the
 * frame says belongs to that feature's dictionary; what the frame itself says
 * belongs here.
 */
export const shellMessages = {
	en: {
		header: {
			benChat: "Chat with Ben",
			openNav: "Open menu",
			/** The logo is a door, and a door with no name is invisible to a screen reader. */
			home: "Benvox — go to the start",
			search: "Search",
			/** `Theme: light. Click for dark.` — the name lives where text costs nothing. */
			theme: "Theme: {name}",
			themeNext: "Theme: {name}. Click for {next}.",
			themes: { light: "light", dark: "dark", system: "the system one" },
		},
		user: {
			trigger: "Your account and this workspace",
			/** Never a workspace-shaped placeholder: whoever runs two accounts would read it as the answer. */
			product: "BenVox",
			inWorkspace: "in workspace {name}",
			roles: { owner: "owner", admin: "admin", member: "member" },
			sound: "Play a sound when a conversation enters the queue",
			account: "Your account",
			signOut: "Sign out",
		},
		quick: {
			pipeline: "Funnels",
			agent: "Agents",
			conversation: "Conversations",
			created: "{name} is in the records.",
		},
		/*
		 * As primeiras palavras de toda sessão SAÍRAM daqui, e do produto: o ecrã de
		 * arranque não diz mais nada — desenha a marca. Era a única frase escrita
		 * antes de qualquer idioma ter sido lido de volta, e a que faltasse
		 * desenharia a própria chave no meio do ecrã. Ver `AppBoot.vue`.
		 */
		/**
		 * The dismiss button of a toast. The message inside one belongs to whoever
		 * raised it; the button belongs to the frame that mounts the stack, which is
		 * this one — the primitive itself may not know a language.
		 */
		toast: { dismiss: "Dismiss notification" },
		/**
		 * THE PARTNER PORTAL'S FRAME — a different world from the panel's.
		 *
		 * Its reader has no workspace and no tenant, so none of `user.*` applies. The
		 * nav labels are here and not in `nav.ts` because that file names the
		 * PRODUCT's screens, which a partner never sees.
		 *
		 * The route paths stay hard-coded at the call site: `/parceiro/...` is a
		 * fixed address, not a word, and translating a URL breaks every link a
		 * partner has bookmarked.
		 */
		partner: {
			portal: "Partner portal",
			navLabel: "Portal",
			nav: {
				home: "Home",
				referrals: "Referrals",
				plans: "Plans",
				invoices: "Invoices",
				commissions: "Commissions",
				resellers: "Resellers",
				blueprints: "Blueprints",
				account: "Account",
			},
			/** Borrowed access is SAID, never hidden — V2 issued an unmarked token. */
			borrowedAs: "You are seeing this as {name}.",
			borrowedUntil: "Borrowed access until {when}.",
			endBorrowed: "end",
			footer:
				"The plans, the invoices and the running of the referred workspaces belong to the installation. What is here is read-only, and yours.",
		},
	},
	"pt-BR": {
		header: {
			benChat: "Chat com Ben",
			openNav: "Abrir menu",
			home: "Benvox — ir para o início",
			search: "Buscar",
			theme: "Tema: {name}",
			themeNext: "Tema: {name}. Clique para {next}.",
			themes: { light: "claro", dark: "escuro", system: "o do sistema" },
		},
		user: {
			trigger: "Sua conta e este workspace",
			product: "BenVox",
			inWorkspace: "no workspace {name}",
			roles: { owner: "dono", admin: "administrador", member: "membro" },
			sound: "Avisar com som quando entrar conversa na fila",
			account: "Sua conta",
			signOut: "Sair",
		},
		quick: {
			pipeline: "Funis",
			agent: "Agentes",
			conversation: "Conversas",
			created: "{name} está no cadastro.",
		},
		toast: { dismiss: "Fechar aviso" },
		partner: {
			portal: "Portal do parceiro",
			navLabel: "Portal",
			nav: {
				home: "Início",
				referrals: "Indicados",
				plans: "Planos",
				invoices: "Faturas",
				commissions: "Comissões",
				resellers: "Revendedores",
				blueprints: "Blueprints",
				account: "Conta",
			},
			borrowedAs: "Você está vendo como {name}.",
			borrowedUntil: "Acesso emprestado até {when}.",
			endBorrowed: "encerrar",
			footer:
				"Os planos, as faturas e a operação dos workspaces indicados são da instalação. Aqui é somente leitura do que é seu.",
		},
	},
	el: {
		header: {
			benChat: "Συνομιλία με τον Ben",
			openNav: "Άνοιγμα μενού",
			home: "Benvox — μετάβαση στην αρχή",
			search: "Αναζήτηση",
			theme: "Θέμα: {name}",
			themeNext: "Θέμα: {name}. Κάνε κλικ για {next}.",
			themes: { light: "φωτεινό", dark: "σκοτεινό", system: "αυτό του συστήματος" },
		},
		user: {
			trigger: "Ο λογαριασμός σου και αυτός ο χώρος εργασίας",
			product: "BenVox",
			inWorkspace: "στον χώρο εργασίας {name}",
			roles: { owner: "ιδιοκτήτης", admin: "διαχειριστής", member: "μέλος" },
			sound: "Ήχος όταν μπαίνει μια συνομιλία στην ουρά",
			account: "Ο λογαριασμός σου",
			signOut: "Αποσύνδεση",
		},
		quick: {
			pipeline: "Χοάνες",
			agent: "Πράκτορες",
			conversation: "Συνομιλίες",
			created: "{name} μπήκε στις εγγραφές.",
		},
		toast: { dismiss: "Κλείσιμο ειδοποίησης" },
		partner: {
			portal: "Πύλη συνεργάτη",
			navLabel: "Πύλη",
			nav: {
				home: "Αρχική",
				referrals: "Παραπομπές",
				plans: "Πλάνα",
				invoices: "Τιμολόγια",
				commissions: "Προμήθειες",
				resellers: "Μεταπωλητές",
				blueprints: "Blueprints",
				account: "Λογαριασμός",
			},
			borrowedAs: "Το βλέπεις αυτό ως {name}.",
			borrowedUntil: "Δανεική πρόσβαση έως {when}.",
			endBorrowed: "τερματισμός",
			footer:
				"Τα πλάνα, τα τιμολόγια και η λειτουργία των χώρων εργασίας που παρέπεμψες ανήκουν στην εγκατάσταση. Ό,τι υπάρχει εδώ είναι μόνο για ανάγνωση, και δικό σου.",
		},
	},
	es: {
		header: {
			benChat: "Chat con Ben",
			openNav: "Abrir menú",
			home: "Benvox — ir al inicio",
			search: "Buscar",
			theme: "Tema: {name}",
			themeNext: "Tema: {name}. Haz clic para {next}.",
			themes: { light: "claro", dark: "oscuro", system: "el del sistema" },
		},
		user: {
			trigger: "Tu cuenta y este espacio de trabajo",
			product: "BenVox",
			inWorkspace: "en el espacio de trabajo {name}",
			roles: { owner: "propietario", admin: "administrador", member: "miembro" },
			sound: "Sonar un aviso cuando entre una conversación en la cola",
			account: "Tu cuenta",
			signOut: "Cerrar sesión",
		},
		quick: {
			pipeline: "Embudos",
			agent: "Agentes",
			conversation: "Conversaciones",
			created: "{name} ya está en los registros.",
		},
		toast: { dismiss: "Cerrar el aviso" },
		partner: {
			portal: "Portal del socio",
			navLabel: "Portal",
			nav: {
				home: "Inicio",
				referrals: "Referidos",
				plans: "Planes",
				invoices: "Facturas",
				commissions: "Comisiones",
				resellers: "Revendedores",
				blueprints: "Blueprints",
				account: "Cuenta",
			},
			borrowedAs: "Estás viendo esto como {name}.",
			borrowedUntil: "Acceso prestado hasta {when}.",
			endBorrowed: "terminar",
			footer:
				"Los planes, las facturas y la operativa de los espacios de trabajo referidos son de la instalación. Lo que hay aquí es de solo lectura, y es tuyo.",
		},
	},
	fr: {
		header: {
			benChat: "Discuter avec Ben",
			openNav: "Ouvrir le menu",
			home: "Benvox — revenir à l'accueil",
			search: "Rechercher",
			theme: "Thème : {name}",
			themeNext: "Thème : {name}. Cliquez pour {next}.",
			themes: { light: "clair", dark: "sombre", system: "celui du système" },
		},
		user: {
			trigger: "Votre compte et cet espace de travail",
			product: "BenVox",
			inWorkspace: "dans l'espace de travail {name}",
			roles: { owner: "propriétaire", admin: "administrateur", member: "membre" },
			sound: "Émettre un son quand une conversation entre dans la file d'attente",
			account: "Votre compte",
			signOut: "Se déconnecter",
		},
		quick: {
			pipeline: "Pipelines",
			agent: "Agents",
			conversation: "Conversations",
			created: "{name} est dans les fiches.",
		},
		toast: { dismiss: "Fermer la notification" },
		partner: {
			portal: "Portail partenaire",
			navLabel: "Portail",
			nav: {
				home: "Accueil",
				referrals: "Parrainages",
				plans: "Forfaits",
				invoices: "Factures",
				commissions: "Commissions",
				resellers: "Revendeurs",
				blueprints: "Blueprints",
				account: "Compte",
			},
			borrowedAs: "Vous voyez ceci en tant que {name}.",
			borrowedUntil: "Accès emprunté jusqu'à {when}.",
			endBorrowed: "terminer",
			footer:
				"Les forfaits, les factures et le fonctionnement des espaces de travail parrainés appartiennent à l'installation. Ce qui est ici est en lecture seule, et vous appartient.",
		},
	},
	it: {
		header: {
			benChat: "Chat con Ben",
			openNav: "Apri il menu",
			home: "Benvox — torna all'inizio",
			search: "Cerca",
			theme: "Tema: {name}",
			themeNext: "Tema: {name}. Clicca per {next}.",
			themes: { light: "chiaro", dark: "scuro", system: "quello di sistema" },
		},
		user: {
			trigger: "Il tuo account e questo spazio di lavoro",
			product: "BenVox",
			inWorkspace: "nello spazio di lavoro {name}",
			roles: { owner: "proprietario", admin: "amministratore", member: "membro" },
			sound: "Riproduci un suono quando una conversazione entra in coda",
			account: "Il tuo account",
			signOut: "Esci",
		},
		quick: {
			pipeline: "Pipeline",
			agent: "Agenti",
			conversation: "Conversazioni",
			created: "{name} è tra i record.",
		},
		toast: { dismiss: "Chiudi la notifica" },
		partner: {
			portal: "Portale partner",
			navLabel: "Portale",
			nav: {
				home: "Home",
				referrals: "Segnalazioni",
				plans: "Piani",
				invoices: "Fatture",
				commissions: "Commissioni",
				resellers: "Rivenditori",
				blueprints: "Blueprint",
				account: "Account",
			},
			borrowedAs: "Stai vedendo questo come {name}.",
			borrowedUntil: "Accesso in prestito fino a {when}.",
			endBorrowed: "termina",
			footer:
				"I piani, le fatture e la gestione degli spazi di lavoro segnalati appartengono all'installazione. Quello che c'è qui è in sola lettura, ed è tuo.",
		},
	},
	de: {
		header: {
			benChat: "Chat mit Ben",
			openNav: "Menü öffnen",
			home: "Benvox — zum Start",
			search: "Suchen",
			theme: "Design: {name}",
			themeNext: "Design: {name}. Für {next} klicken.",
			themes: { light: "hell", dark: "dunkel", system: "Systemvorgabe" },
		},
		user: {
			trigger: "Ihr Konto und dieser Workspace",
			product: "BenVox",
			inWorkspace: "im Workspace {name}",
			roles: { owner: "Inhaber", admin: "Administrator", member: "Mitglied" },
			sound: "Ton abspielen, wenn eine Konversation in die Warteschlange kommt",
			account: "Ihr Konto",
			signOut: "Abmelden",
		},
		quick: {
			pipeline: "Pipelines",
			agent: "Agenten",
			conversation: "Konversationen",
			created: "{name} steht in den Datensätzen.",
		},
		toast: { dismiss: "Benachrichtigung schließen" },
		partner: {
			portal: "Partnerportal",
			navLabel: "Portal",
			nav: {
				home: "Start",
				referrals: "Empfehlungen",
				plans: "Tarife",
				invoices: "Rechnungen",
				commissions: "Provisionen",
				resellers: "Wiederverkäufer",
				blueprints: "Blueprints",
				account: "Konto",
			},
			borrowedAs: "Sie sehen dies als {name}.",
			borrowedUntil: "Geliehener Zugang bis {when}.",
			endBorrowed: "beenden",
			footer:
				"Die Tarife, die Rechnungen und der Betrieb der empfohlenen Workspaces gehören zur Installation. Was hier steht, ist schreibgeschützt und gehört Ihnen.",
		},
	},
} as const;
